import * as DG from "ui/dialogs"
import { useEffect, useState } from "react"

import { ApiError, ErrorsMap } from "api/http/utils"
import { createMarketTarget, fetchMarketTargetParams } from "api/targets"
import { css, cx } from "@emotion/css"
import { queryCache, useMutation, useQuery } from "react-query"

import Button from "ui/buttons/Button"
import { CreateMarketTarget } from "models/targets"
import FormControl from "@material-ui/core/FormControl"
import DropdownIcon from "@material-ui/icons/KeyboardArrowDown"
import { MultiSelect } from "routes/Base/Admin/components/MultiSelect"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { InputAdornment } from "@material-ui/core"

export type Props = {
  open?: boolean
  onCancel?: () => void
}

export const CreateTargetModal = (props: Props) => {
  const { open, onCancel } = props
  const { t } = useI18n()

  const [state, setState] = useState<Partial<CreateMarketTarget>>()
  const [errors, setErrors] = useState<ApiError>()
  const [fieldError, setFieldError] = useState<ErrorsMap>()
  const { meta } = useAuthenticated()
  const marketId = meta?.layerId ?? ""
  const [openDropdown, setOpenDropdown] = useState<string>("")

  const [create] = useMutation(createMarketTarget, {
    onSuccess: () => {
      queryCache.invalidateQueries("targets")
      handleClose()
    },
    onError: (err: ApiError) => {
      const errorMap = err?.errorsMap ?? {}
      !Object.keys(errorMap).length ? setErrors(err) : setFieldError(errorMap)
    },
  })

  const handleSubmit = () => {
    const pld = {
      variables: {
        SOCIOECONOMICS: state?.SOCIOECONOMICS,
        AGE_RANGE: state?.AGE_RANGE,
        GENDER: state?.GENDER,
        TYPE_OF_WORK: state?.TYPE_OF_WORK,
      },
    } as Partial<CreateMarketTarget>
    create({ ...pld })
  }

  const handleClose = () => {
    setState(undefined)
    setErrors(undefined)
    setFieldError(undefined)
    onCancel && onCancel()
  }

  const handleToggleOption =
    (semantic: string) =>
    (selectedOptions: { label: string; value: string }[]) => {
      if (!!selectedOptions?.length) {
        setErrors(undefined)
        setFieldError(undefined)
        setState((s) => ({
          ...s,
          [semantic]: selectedOptions.map((o) => o.value),
        }))
      } else {
        setErrors(undefined)
        setFieldError(undefined)
        setState((s) => {
          const { [semantic as keyof CreateMarketTarget]: remove, ...rest } =
            s ?? {}
          return !!Object.keys(rest)?.length ? rest : undefined
        })
      }
    }

  const handleSelectOpen = (field: string) => setOpenDropdown(field)

  const handleSelectClose = () => setOpenDropdown("")

  const { data: targetParams = [] } = useQuery(
    ["target-params", marketId],
    fetchMarketTargetParams,
  )

  const genderOptions = targetParams
    ?.find((t) => t.semantic === "GENDER")
    ?.variables?.map((p) => ({
      label: p,
      value: p,
    }))

  const ageRangeOptions = targetParams
    ?.find((t) => t.semantic === "AGE_RANGE")
    ?.variables.map((p) => ({
      label: p,
      value: p,
    }))
  const socioeconomicsOptions = targetParams
    ?.find((t) => t.semantic === "SOCIOECONOMICS")
    ?.variables.map((p) => ({
      label: p,
      value: p,
    }))
  const typeOfWorkOptions = targetParams
    ?.find((t) => t.semantic === "TYPE_OF_WORK")
    ?.variables.map((p) => ({
      label: p,
      value: p,
    }))

  const id = "create-new-target"

  useEffect(() => {
    if (!open) {
      setState(undefined)
      setErrors(undefined)
    }
  }, [open])

  const canCreate =
    !!state?.AGE_RANGE ||
    !!state?.GENDER ||
    !!state?.SOCIOECONOMICS ||
    !!state?.TYPE_OF_WORK

  return (
    <DG.Dialog
      className={styles.dialog}
      open={open || false}
      onClose={handleClose}
      id={id}
      disableEscapeKeyDown
      keepMounted
      BackdropProps={{ style: { backgroundColor: "rgba(0, 0, 0, 0.3)" } }}
    >
      <DG.DialogHeader>
        <DG.DialogTitle id={id}>
          {t("market.target.create.title")}
        </DG.DialogTitle>
        <DG.DialogClose onClose={handleClose} />
      </DG.DialogHeader>

      <DG.DialogContent className={styles.content}>
        <div className={styles.inputWrapper}>
          <FormControl variant="outlined">
            <div>
              <MultiSelect
                label={t("market.target.input.socioeconomics")}
                placeholder={t("market.target.input.socioeconomics")}
                items={socioeconomicsOptions || []}
                getOptionLabel={(option) => option?.label || ""}
                selectedValues={
                  state?.["SOCIOECONOMICS"]?.map((v) => ({
                    label: v,
                    value: v,
                  })) || []
                }
                inputValue={state?.SOCIOECONOMICS?.join(",") || ""}
                checkValues={() => true}
                errorMessage={
                  !!fieldError && fieldError["variables[SOCIOECONOMICS]"]
                    ? t(fieldError["variables[SOCIOECONOMICS]"])
                    : undefined
                }
                renderOption={(option) => option?.label}
                onToggleOption={handleToggleOption("SOCIOECONOMICS")}
                onOpen={() => handleSelectOpen("SOCIOECONOMICS")}
                open={openDropdown === "SOCIOECONOMICS"}
                onClose={handleSelectClose}
                className={
                  !!fieldError && fieldError["variables[SOCIOECONOMICS]"]
                    ? styles.inputError
                    : styles.input
                }
                endAdornment={
                  <DropDownAdornment
                    name="SOCIOECONOMICS"
                    open={openDropdown}
                  />
                }
              />
              <MultiSelect
                label={t("market.target.input.age")}
                placeholder={t("market.target.input.age")}
                items={ageRangeOptions || []}
                getOptionLabel={(option) => option?.label || ""}
                selectedValues={
                  state?.["AGE_RANGE"]?.map((v) => ({ label: v, value: v })) ||
                  []
                }
                inputValue={state?.AGE_RANGE?.join(",") || ""}
                checkValues={() => true}
                errorMessage={
                  !!fieldError && fieldError["variables[AGE_RANGE]"]
                    ? t(fieldError["variables[AGE_RANGE]"])
                    : undefined
                }
                renderOption={(option) => option?.label}
                onToggleOption={handleToggleOption("AGE_RANGE")}
                onOpen={() => handleSelectOpen("AGE_RANGE")}
                open={openDropdown === "AGE_RANGE"}
                onClose={handleSelectClose}
                className={
                  !!fieldError && fieldError["variables[AGE_RANGE]"]
                    ? styles.inputError
                    : styles.input
                }
                endAdornment={
                  <DropDownAdornment name="AGE_RANGE" open={openDropdown} />
                }
              />
              <MultiSelect
                label={t("market.target.input.gender")}
                placeholder={t("market.target.input.gender")}
                items={genderOptions || []}
                getOptionLabel={(option) => option?.label || ""}
                selectedValues={
                  state?.["GENDER"]?.map((v) => ({ label: v, value: v })) || []
                }
                inputValue={state?.GENDER?.join(",") || ""}
                checkValues={() => true}
                errorMessage={
                  !!fieldError && fieldError["variables[GENDER]"]
                    ? t(fieldError["variables[GENDER]"])
                    : undefined
                }
                renderOption={(option) => option?.label}
                onToggleOption={handleToggleOption("GENDER")}
                onOpen={() => handleSelectOpen("GENDER")}
                open={openDropdown === "GENDER"}
                onClose={handleSelectClose}
                className={
                  !!fieldError && fieldError["variables[GENDER]"]
                    ? styles.inputError
                    : styles.input
                }
                endAdornment={
                  <DropDownAdornment name="GENDER" open={openDropdown} />
                }
              />
              <MultiSelect
                label={t("market.target.input.type")}
                placeholder={t("market.target.input.type")}
                items={typeOfWorkOptions || []}
                getOptionLabel={(option) => option?.label || ""}
                selectedValues={
                  state?.["TYPE_OF_WORK"]?.map((v) => ({
                    label: v,
                    value: v,
                  })) || []
                }
                inputValue={state?.TYPE_OF_WORK?.join(",") || ""}
                checkValues={() => true}
                errorMessage={
                  !!fieldError && fieldError["variables[TYPE_OF_WORK]"]
                    ? t(fieldError["variables[TYPE_OF_WORK]"])
                    : undefined
                }
                renderOption={(option) => option?.label}
                onToggleOption={handleToggleOption("TYPE_OF_WORK")}
                onOpen={() => handleSelectOpen("TYPE_OF_WORK")}
                open={openDropdown === "TYPE_OF_WORK"}
                onClose={handleSelectClose}
                className={
                  !!fieldError && fieldError["variables[TYPE_OF_WORK]"]
                    ? styles.inputError
                    : styles.input
                }
                endAdornment={
                  <DropDownAdornment name="TYPE_OF_WORK" open={openDropdown} />
                }
              />
            </div>
          </FormControl>
          {!!errors?.message && (
            <div className={cx("BodyBody-xsRedRegular", styles.genericError)}>
              {t(errors?.message)}
            </div>
          )}
        </div>
      </DG.DialogContent>
      <DG.DialogFooter>
        <DG.DialogActions>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            {t("market.target.button.cancel")}
          </Button>
          <Button
            variant="contained"
            color="primary"
            type="submit"
            form="new_target"
            onClick={handleSubmit}
            disabled={!canCreate}
          >
            {t("market.target.button.create")}
          </Button>
        </DG.DialogActions>
      </DG.DialogFooter>
    </DG.Dialog>
  )
}
const DropDownAdornment = ({ name, open }: { name: string; open: string }) => {
  return (
    <InputAdornment
      position="end"
      className={cx({ [styles.adornment]: !!open })}
    >
      {<DropdownIcon className={styles.dropdown} />}
    </InputAdornment>
  )
}

const styles = {
  inputWrapper: css`
    margin-right: auto;
    margin-top: var(--spacing-xl);
  `,
  input: css`
    width: 256px;
    margin-bottom: var(--spacing-xl);
    > div > div {
      width: 100%;
    }
  `,

  inputError: css`
    min-width: 255px;
    padding-bottom: var(--spacing-xs);
  `,
  error: css`
    padding: 0 0 var(--spacing-xs) var(--spacing-m);
  `,
  genericError: css`
    padding: 0 0 0 var(--spacing-m);
  `,
  content: css`
    padding-bottom: var(--spacing-xl);
  `,
  dialog: css`
    max-width: 551px;
    margin: auto;
  `,
  dropdown: css`
    color: var(--dark-grey);
  `,
  adornment: css`
    transform: rotate(180deg);
  `,
}
